import { RequireScope } from '@/components/RequireScope'
import { CrossroadsConfiguration } from '@/pages/CrossroadsConfiguration'
import { Dashboard } from '@/pages/Dashboard/Dashboard'
import { DashboardsPage } from '@/pages/Dashboards'
import { MasterDataManagement } from '@/pages/MasterData/MasterDataManagement'
import { OrderDetails } from '@/pages/OrderManagement/OrderDetails'
import { OrderManagement } from '@/pages/OrderManagement/OrderManagement'
import { SupplyPlansPage } from '@/pages/SupplyPlans'
import { TankReadingsPage } from '@/pages/TankReadings'
import { TenantConfiguration } from '@/pages/TenantConfiguration'
import { UserManagementPage } from '@/pages/Users/UserManagement/page'
import { ApiOutlined, SettingOutlined, TruckOutlined } from '@ant-design/icons'
import type { Page, Scope } from '@gravitate-js/excalibrr'
import { ChangeOwnershipPage } from '@pages/Tools/ChangeOwnership/ChangeOwnershipPage'
import { IoHammerOutline } from 'react-icons/io5'
import { Outlet } from 'react-router-dom'

type PageConfig = Record<string, Page>

// TODO: The concept of user scopes doesn't really exist right now and isn't coming back on the /me endpoint.
// Until we have that we can't drive page visibility based on scopes, so everything is visible atm.
function hasGravitateAccess(_scopes: Record<string, Scope>): boolean {
  return true
  // return (
  //   scopes?.crossroads?.view === true ||
  //   scopes?.masterdata?.view === true ||
  //   scopes?.users?.view === true ||
  //   scopes?.network?.view === true
  // )
}

// Whether the user has a given scope. Scopes are surfaced as boolean flags by
// SecurityContext.getScopes() from the user's JWT scopes claim.
function hasScope(scopes: Record<string, Scope>, scope: string): boolean {
  return scopes?.[scope] === true
}

export const pageConfig: PageConfig = {
  operations: {
    key: 'operations',
    title: 'Business Operations',
    icon: <TruckOutlined />,
    element: <Outlet />,
    hasPermission: () => true,
    defaultRedirect: 'orders',
    routes: [
      {
        key: 'orders',
        title: 'Order Management',
        element: (
          <RequireScope scope='order_management'>
            <OrderManagement />
          </RequireScope>
        ),
        hasPermission: () => true,
      },
      {
        key: 'orders/:id',
        title: 'Order Details',
        element: (
          <RequireScope scope='order_management'>
            <OrderDetails />
          </RequireScope>
        ),
        hasPermission: () => true,
      },
      {
        key: 'tank-readings',
        title: 'Tank Readings',
        element: (
          <RequireScope scope='order_management'>
            <TankReadingsPage />
          </RequireScope>
        ),
        hasPermission: () => true,
      },
      {
        key: 'supply-plans',
        title: 'Supply Plans',
        element: (
          <RequireScope scope='order_management'>
            <SupplyPlansPage />
          </RequireScope>
        ),
        hasPermission: () => true,
      },
    ],
  },
  dashboard: {
    key: 'integration-management',
    title: 'Integration Management',
    icon: <ApiOutlined />,
    element: <Outlet />,
    hasPermission: () => true,
    defaultRedirect: 'overview',
    routes: [
      {
        key: 'overview',
        title: 'Dashboards',
        element: <DashboardsPage />,
        hasPermission: () => true,
      },
      {
        key: 'integration-logs',
        title: 'Integration Logs',
        element: <Outlet />,
        hasPermission: () => true,
        routes: [
          {
            key: 'connection-logs',
            title: 'Orders',
            element: <Dashboard />,
            hasPermission: () => true,
          },
          {
            key: 'supply-plan-logs',
            title: 'Supply Plans',
            element: <Dashboard />,
            hasPermission: () => true,
          },
        ],
      },
      {
        key: 'setup',
        title: 'Setup',
        // Group label only -- see the note on 'integration-logs' above.
        element: <Outlet />,
        hasPermission: () => true,
        routes: [
          {
            key: 'reference-data',
            title: 'Reference Data',
            element: <Dashboard />,
            hasPermission: () => true,
          },
          {
            key: 'tenants',
            title: 'Tenant Configuration',
            element: <TenantConfiguration />,
            hasPermission: (scopes: Record<string, Scope>) =>
              hasScope(scopes, 'tenant_configuration'),
          },
        ],
      },
    ],
  },
  crossroads: {
    key: 'crossroads',
    title: 'Crossroads Management',
    icon: <SettingOutlined />,
    element: <Outlet />,
    hasPermission: (scopes: Record<string, Scope>) =>
      hasGravitateAccess(scopes),
    defaultRedirect: 'monitoring',
    routes: [
      {
        key: 'users',
        title: 'User Management',
        element: <UserManagementPage />,
        hasPermission: (scopes: Record<string, Scope>) =>
          hasGravitateAccess(scopes),
      },
      {
        key: 'masterdata',
        title: 'Master Reference Data',
        element: <MasterDataManagement />,
        hasPermission: (scopes: Record<string, Scope>) =>
          hasGravitateAccess(scopes),
      },
      {
        key: 'configuration',
        title: 'Configuration',
        element: <CrossroadsConfiguration />,
        hasPermission: (scopes: Record<string, Scope>) =>
          hasGravitateAccess(scopes),
      },
    ],
  },
  tools: {
    key: 'tools',
    title: 'Tools',
    icon: <IoHammerOutline />,
    element: <Outlet />,
    // No hasPermission: excalibrr never calls a section's own check, so this
    // section is gated by its 'tools' key in SecurityContext.getScopes().
    defaultRedirect: 'change_ownership',
    routes: [
      {
        key: 'change_ownership',
        title: 'Change MRD Ownership',
        element: <ChangeOwnershipPage />,
        // Per-tool gate. The 'tools' section itself is gated on use_tools in
        // SecurityContext.getScopes().
        hasPermission: (scopes: Record<string, Scope>) =>
          hasScope(scopes, 'crossroads_entities.edit'),
      },
    ],
  },
}
